import React, { useState, useEffect, ChangeEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { categories, Category, SubCategory, findCategoryAndSubcategory } from '../../../routes/formatterRoute';
import SEO from './../../../components/SEO';
import { ArrowRight, ChevronRight, Search } from 'lucide-react';
import { trackEvent } from './../../../utils/GoogleAnalytics';
import { pushGTMEvent } from '../../../utils/GoogleTagManager';

// Import components directly 
import JsonFormatter from './Data/JsonFormatter';
import XmlFormatter from './Data/XmlFormatter';
import YamlFormatter from './Data/YamlFormatter';
import SqlFormatter from './Data/SqlFormatter';
import IniFormatter from './Data/IniFormatter';

import HTMLFormatter from './Data/HTMLFormatter';
import TypeScriptFormatter from './Data/TypeScriptFormatter';
import JavaScriptFormatter from './Data/JavaScriptFormatter';
import CSSFormatter from './Data/CSSFormatter';
import PythonFormatter from './Data/PythonFormatter';
import PHPFormatter from './Data/PhpFormatter';
import JavaFormatter from './Data/JavaFormatter';
import CppFormatter from './Data/CppFormatter';



import WorkInProgress from './../../common/WorkInProgress';

// Component map for category/subcategory structure
const converterComponents: Record<string, Record<string, React.ComponentType<any>>> = {
  'data-formatters': {
    'json-formatter': JsonFormatter,
    'xml-formatter': XmlFormatter,
    'yaml-formatter': YamlFormatter,
    'sql-formatter': SqlFormatter,
    'ini-formatter': IniFormatter,
  },
  'code-formatters': {
    'html-formatter': HTMLFormatter,
    'typescript-formatter': TypeScriptFormatter,
    'javascript-formatter': JavaScriptFormatter,
    'css-formatter': CSSFormatter,
    'python-formatter': PythonFormatter,
    'php-formatter': PHPFormatter,
    'java-formatter': JavaFormatter,
    'cpp-formatter': CppFormatter,
  },
  'text-document-formatters': {
    'markdown-formatter': WorkInProgress,
    'latex-formatter': WorkInProgress,
    'text-wrap-formatter': WorkInProgress,
  },
  'specialized-formatters': {
    'log-formatter': WorkInProgress,
    'jwt-formatter': WorkInProgress,
    'url-formatter': WorkInProgress,
    'base64-formatter': WorkInProgress,
    'uuid-formatter': WorkInProgress,
  }
};

// Direct route mapping for simplified URLs
const directRouteMap: Record<string, { categoryId: string; subcategoryId: string; component: React.ComponentType<any> | null }> = {
  'json-formatter': { categoryId: 'data-formatters', subcategoryId: 'json-formatter', component: JsonFormatter },
  'xml-formatter': { categoryId: 'data-formatters', subcategoryId: 'xml-formatter', component: XmlFormatter },
  'yaml-formatter': { categoryId: 'data-formatters', subcategoryId: 'yaml-formatter', component: YamlFormatter },
  'sql-formatter': { categoryId: 'data-formatters', subcategoryId: 'sql-formatter', component: SqlFormatter },
  'ini-formatter': { categoryId: 'data-formatters', subcategoryId: 'ini-formatter', component: IniFormatter },

  'html-formatter': { categoryId: 'code-formatters', subcategoryId: 'html-formatter', component: HTMLFormatter },
  'typescript-formatter': { categoryId: 'code-formatters', subcategoryId: 'typescript-formatter', component: WorkInProgress },
  'javascript-formatter': { categoryId: 'code-formatters', subcategoryId: 'javascript-formatter', component: JavaScriptFormatter },
  'css-formatter': { categoryId: 'code-formatters', subcategoryId: 'css-formatter', component: CSSFormatter },
  'python-formatter': { categoryId: 'code-formatters', subcategoryId: 'python-formatter', component: PythonFormatter },
  'php-formatter': { categoryId: 'code-formatters', subcategoryId: 'php-formatter', component: PHPFormatter },
  'java-formatter': { categoryId: 'code-formatters', subcategoryId: 'java-formatter', component: JavaFormatter },
  'cpp-formatter': { categoryId: 'code-formatters', subcategoryId: 'cpp-formatter', component: WorkInProgress },

  'markdown-formatter': { categoryId: 'text-document-formatters', subcategoryId: 'markdown-formatter', component: WorkInProgress },
  'latex-formatter': { categoryId: 'text-document-formatters', subcategoryId: 'latex-formatter', component: WorkInProgress },
  'text-wrap-formatter': { categoryId: 'text-document-formatters', subcategoryId: 'text-wrap-formatter', component: WorkInProgress },

  'log-formatter': { categoryId: 'specialized-formatters', subcategoryId: 'log-formatter', component: WorkInProgress },
  'jwt-formatter': { categoryId: 'specialized-formatters', subcategoryId: 'jwt-formatter', component: WorkInProgress },
  'url-formatter': { categoryId: 'specialized-formatters', subcategoryId: 'url-formatter', component: WorkInProgress },
  'base64-formatter': { categoryId: 'specialized-formatters', subcategoryId: 'base64-formatter', component: WorkInProgress },
  'uuid-formatter': { categoryId: 'specialized-formatters', subcategoryId: 'uuid-formatter', component: WorkInProgress },


  // Add other components as they become available
};

// Type definitions
type ConverterComponentsType = {
  [category: string]: {
    [subcategory: string]: React.ComponentType<any>;
  }
};

// Direct route mapping
type DirectRouteMapType = {
  [path: string]: {
    categoryId: string;
    subcategoryId: string;
    component: React.ComponentType<any>;
  }
};

// Loading component
const LoadingFallback = () => (
  <div className="flex items-center justify-center h-full">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
  </div>
);

// Fallback component when no converter is found
const ConverterNotFound = ({ categoryId, subcategoryId }: { categoryId?: string, subcategoryId?: string }) => (
  <div className="max-w-4xl mx-auto">
    <h1 className="text-2xl font-bold mb-6">Converter Not Found</h1>
    <div className="p-4 mb-6 bg-yellow-50 border border-yellow-200 text-yellow-700 rounded-md">
      <p className="mb-2">
        <strong>The requested converter could not be found.</strong>
      </p>
      <p>
        {categoryId && subcategoryId ? 
          `We couldn't find a converter for ${categoryId}/${subcategoryId}.` :
          'No converter was specified in the URL.'}
      </p>
    </div>
  </div>
);

const Formatter: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [query, setQuery] = useState<string>('');
  const [filteredData, setFilteredData] = useState<Category[]>([]);

  const ScrollToTop = () => {
    useEffect(() => {
      // Scroll to top when component mounts
      window.scrollTo(0, 0);
    }, []);
  
    return null; // This component doesn't render anything
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLowerCase();
    setQuery(value);

    if (value === '') {
      setFilteredData([]);
      return;
    }

    const results = categories
      .map(category => {
        const matchingSubCategories = category.subCategories.filter(sub =>
          sub.name.toLowerCase().includes(value)
        );
        if (matchingSubCategories.length > 0) {
          return {
            ...category,
            subCategories: matchingSubCategories
          };
        }
        return null;
      })
      .filter((cat): cat is Category => cat !== null);

    setFilteredData(results);
  };

  // Extract path information from URL
  const getRouteInfo = () => {
    // First check for direct paths
    const directPath = location.pathname.split('/').pop() || '';
    const isDirectRoute = directPath in directRouteMap;
    
    if (isDirectRoute) {
      const route = directRouteMap[directPath];
      return {
        isDirectRoute,
        directPath,
        categoryId: route.categoryId,
        subcategoryId: route.subcategoryId
      };
    }
    
    // Fall back to traditional URL structure
    const urlParts = location.pathname.split('/').filter(Boolean);
    
    // Check for /formatter/category/subcategory pattern
    if (urlParts[0] === 'formatter' && urlParts.length > 1) {
      return {
        isDirectRoute: false,
        directPath: '',
        categoryId: urlParts[1],
        subcategoryId: urlParts.length > 2 ? urlParts[2] : ''
      };
    }
    
    // Default case - no recognized pattern
    return {
      isDirectRoute: false,
      directPath: '',
      categoryId: '',
      subcategoryId: ''
    };
  };

  const routeInfo = getRouteInfo();
  const { isDirectRoute, directPath, categoryId, subcategoryId } = routeInfo;

  // Debug logs
  useEffect(() => {
    console.log('Current URL:', location.pathname);
    console.log('Route info:', routeInfo);
  }, [location.pathname]);

  // UI States
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  // Handle responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // State for categories with their expansion status
  const [categoriesState, setCategoriesState] = useState<Category[]>(() => {
    // Initialize with categories from the routes file and set isOpen based on current route
    return categories.map(category => ({
      ...category,
      isOpen: category.id === categoryId || Boolean(category.isOpen)
    }));
  });

  // Update categories isOpen state when URL params change
  useEffect(() => {
    setCategoriesState(prevCategories =>
      prevCategories.map(category => ({
        ...category,
        isOpen: category.id === categoryId || Boolean(category.isOpen)
      }))
    );
  }, [categoryId]);

  // Toggle category expansion
  const toggleCategory = (catId: string) => {
    setCategoriesState(prevState =>
      prevState.map(category =>
        category.id === catId
          ? { ...category, isOpen: !Boolean(category.isOpen) }
          : category
      )
    );
  };

  // Navigate to a subcategory
  const navigateToSubcategory = (categoryId: string, subcategoryId: string, name: string) => {
    trackEvent('Convert Page', 'Click', 'Navigate to Subcategory');
    pushGTMEvent({
      event: 'convert_page',
      category: 'Click Subcategory',
      action: 'Click',
      label: name,
    });

    // Check if a direct route exists for this subcategory
    const directRoute = Object.entries(directRouteMap).find(
      ([_, value]) => value.categoryId === categoryId && value.subcategoryId === subcategoryId
    );

    if (directRoute) {
      // Use the simplified URL if we found a mapping
      navigate(`/${directRoute[0]}`);
    } else {
      // Fall back to the traditional URL structure
      navigate(`/formatter/${categoryId}/${subcategoryId}`);
    }

    // Close sidebar on mobile after navigation
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  // Find current selected items
  const {
    category: currentCategory = null,
    subcategory: currentSubcategory = null
  } = findCategoryAndSubcategory(categoryId, subcategoryId) || {};

  // Function to check if a category is selected
  const isCategorySelected = (category: Category) => {
    return category.id === categoryId;
  };

  // Function to check if a subcategory is selected
  const isSubcategorySelected = (catId: string, subCatId: string) => {
    // Check both URL patterns
    return (
      (location.pathname.includes(`/formatter/${catId}/${subCatId}`)) ||
      Object.entries(directRouteMap).some(
        ([path, mapping]) =>
          location.pathname.includes(`/${path}`) &&
          mapping.categoryId === catId &&
          mapping.subcategoryId === subCatId
      )
    );
  };

  // Generate SEO metadata
  const generateSEOMetadata = () => {
    if (currentCategory && currentSubcategory) {
      const toolName = currentSubcategory.name;
      const pageTitle = `${toolName} - Free Online Data Conversion Tool`;

      let description = "";
      if (toolName.includes(" to ")) {
        const [from, to] = toolName.split(" to ");
        description = `Convert ${from} data to ${to} format online for free. Easy-to-use ${from} to ${to} converter with no installation required.`;
      } else if (toolName.includes("Formatter")) {
        const format = toolName.split(" ")[0];
        description = `Format and beautify your ${format} data with our free online ${format} formatter. Clean, indent, and structure your ${format} for better readability.`;
      } else {
        description = `Work with ${currentCategory.name} data using our free online ${toolName} tool. No signup required, instant results.`;
      }

      const keywords = `${toolName.toLowerCase()}, ${toolName.toLowerCase().replace(" to ", " converter, ")}, ${currentCategory.name.toLowerCase()} converter, online converter`;

      return {
        title: pageTitle,
        description: description,
        keywords: keywords,
        canonicalUrl: window.location.href,
        ogTitle: pageTitle,
        ogDescription: description,
        ogType: "website",
        twitterCard: "summary_large_image",
        twitterTitle: pageTitle,
        twitterDescription: description
      };
    } else if (currentCategory) {
      // Category page
      const pageTitle = `${currentCategory.name} Converters - Free Online Tools`;
      const description = `Free online tools to convert ${currentCategory.name} data to various formats. Convert, format and transform your ${currentCategory.name} data with ease.`;
      const keywords = `${currentCategory.name.toLowerCase()} converters, ${currentCategory.name.toLowerCase()} formatter, ${currentCategory.name.toLowerCase()} tools, online converter`;

      return {
        title: pageTitle,
        description: description,
        keywords: keywords,
        canonicalUrl: window.location.href,
        ogTitle: pageTitle,
        ogDescription: description
      };
    }

    // Default for home page
    return {
      title: "Data Converters - Free Online Data Format Conversion Tools",
      description: "Transform your data between various formats including JSON, CSV, SQL, XML, and YAML with our free online data converters.",
      keywords: "data converter, json to csv, csv to json, sql formatter, xml formatter, yaml converter",
      canonicalUrl: window.location.href
    };
  };

  const seoProps = generateSEOMetadata();

  // Render the main content area based on the current state
  const renderMainContent = () => {
    // console.log('Rendering main content with:', { categoryId, subcategoryId, isDirectRoute, directPath });

    // First check if we're using a direct route
    if (isDirectRoute && directPath in directRouteMap) {
      const route = directRouteMap[directPath];
      if (route.component) {
        const DirectComponent = route.component;
        return <DirectComponent />;
      }
    }

    // Otherwise fall back to the category/subcategory pattern
    if (categoryId && subcategoryId && categoryId in converterComponents) {
      // Check if the converter exists in our component map
      const converterCategory = converterComponents[categoryId];
      if (subcategoryId in converterCategory) {
        const ConverterComponent = converterCategory[subcategoryId];
        return <ConverterComponent />;
      } else {
        console.warn(`Converter not found for ${categoryId}/${subcategoryId}`);
        return <ConverterNotFound categoryId={categoryId} subcategoryId={subcategoryId} />;
      }
    }

    // Default home content or category landing page
    return (
      <div className="min-h-screen from-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Search bar */}
          <div className="max-w-lg mx-auto mb-12 relative">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search size={20} className="text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search data converters..."
                value={query}
                onChange={handleInputChange}
                className="w-full pl-10 pr-4 py-3 text-base rounded-xl border border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white transition-all duration-200"
              />
            </div>

            {filteredData?.length > 0 && (
              <div className="absolute mt-2 w-full bg-white border border-gray-200 rounded-xl shadow-lg z-10 overflow-hidden">
                {filteredData?.map(category => (
                  <div key={category.id} className="border-b border-gray-100 last:border-b-0">
                    <div className="px-4 py-3 bg-gray-50">
                      <span className="font-medium text-gray-800">{category.name}</span>
                    </div>
                    <ul className="py-2">
                      {category?.subCategories.map(sub => (
                        <li
                          key={sub.id}
                          className="px-4 py-2 hover:bg-blue-50 flex items-center justify-between cursor-pointer group"
                          onClick={() => {
                            setQuery('');
                            navigate(`/${sub?.id}`);
                          }}
                        >
                          <span className="text-gray-700 group-hover:text-blue-700 transition-colors">
                            {sub.name}
                          </span>
                          <ChevronRight size={16} className="text-gray-400 group-hover:text-blue-500 transition-colors" />
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Main content */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-extrabold text-gray-800 mb-4 tracking-tight">
              {currentCategory ? `${currentCategory.name}` : 'Data Conversion Tools'}
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {currentCategory
                ? `Transform your ${currentCategory.name.toLowerCase()} quickly and easily with our conversion tools.`
                : 'Simple, powerful tools to convert your data between formats. No installation required.'}
            </p>
          </div>

          {!currentCategory ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {categories.map(category => (
                <div
                  key={category.id}
                  className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition duration-300 cursor-pointer"
                  onClick={() => {
                    trackEvent('Convert Page', 'Click', 'Navigate to Category');
                    navigate(`/formatter/${category.id}`);
                  }}
                >
                  <div className="p-6">
                    <h2 className="text-xl font-semibold text-gray-800 mb-2">{category.name}</h2>
                    <p className="text-gray-500 mb-4">{category.subCategories.length} conversion tools</p>

                    <div className="space-y-1 mb-4">
                      {category.subCategories.slice(0, 2).map(sub => (
                        <div key={sub.id} className="text-sm text-gray-600 flex items-center">
                          <div className="w-1 h-1 rounded-full bg-gray-400 mr-2"></div>
                          {sub.name}
                        </div>
                      ))}
                      {category.subCategories.length > 2 && (
                        <div className="text-sm text-gray-500">
                          +{category.subCategories.length - 2} more tools
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="bg-gray-50 px-6 py-3 flex justify-end">
                    <div className="flex items-center text-blue-600 font-medium text-sm group">
                      <span className="group-hover:underline">Explore</span>
                      <ArrowRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row max-w-6xl mx-auto">
              <div className="max-w-6xl mx-auto">
                <div className="mb-6">
                  <h2 className="text-2xl font-semibold text-gray-800">{currentCategory.name}</h2>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {currentCategory.subCategories.map(sub => (
                    <div
                      key={sub.id}
                      className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200 cursor-pointer"
                      onClick={() => navigateToSubcategory(currentCategory.id, sub.id, sub.name)}
                    >
                      <div className="p-4 flex items-center justify-between">
                        <span className="text-gray-700 font-medium">{sub.name}</span>
                        <ChevronRight size={18} className="text-gray-400" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Features section */}
          {!currentCategory && (
            <div className="mt-24 text-center">
              <h2 className="text-2xl font-bold text-gray-800 mb-12">Why Choose Our Conversion Tools?</h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                  <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-gray-800 mb-2">Fast & Efficient</h3>
                  <p className="text-gray-600 text-sm">Convert your data instantly with our optimized tools. No waiting or processing delays.</p>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                  <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-gray-800 mb-2">Secure & Private</h3>
                  <p className="text-gray-600 text-sm">All conversions happen in your browser. Your data never leaves your device.</p>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                  <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-gray-800 mb-2">100% Free</h3>
                  <p className="text-gray-600 text-sm">All our conversion tools are completely free to use with no hidden fees or subscriptions.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

    useEffect(() => {
      const handleResize = () => {
        if (window.innerWidth < 768) { // Mobile view (less than md breakpoint)
          setSidebarOpen(false);
        } else { // Desktop view
          setSidebarOpen(true);
        }
      };
  
      // Set initial state
      handleResize();
  
      // Add event listener
      window.addEventListener('resize', handleResize);
  
      // Cleanup
      return () => window.removeEventListener('resize', handleResize);
    }, []);

  return (
    <>
    <ScrollToTop />
      <SEO {...seoProps} />
      <div className="flex h-full bg-gray-100 relative">
        {/* Sidebar - controlled by sidebarOpen state */}
        <div
          className={`
    bg-white shadow-md overflow-y-auto
    transition-all duration-300 ease-in-out
    ${sidebarOpen ? 'w-64' : 'w-0 overflow-hidden'}
    ${sidebarOpen ? 'block' : 'hidden md:block'}
  `}
        >
          <div className="h-full">
            <nav className="py-2">
              {categoriesState.map(category => (
                <div key={category.id} className="mb-1">
                  <button
                    className={`
                      flex items-center justify-between w-full px-4 py-3 text-left
                      ${isCategorySelected(category) ? 'text-blue-600 font-medium' : 'text-gray-700'}
                      hover:bg-gray-50 focus:outline-none
                    `}
                    onClick={() => toggleCategory(category.id)}
                  >
                    <span className="text-base">{category.name}</span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className={`
                        h-4 w-4 transition-transform duration-200 ease-in-out
                        ${category.isOpen ? 'transform rotate-180' : ''}
                      `}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* Subcategories with smooth height animation */}
                  <div
                    className={`
                      overflow-hidden transition-all duration-300 ease-in-out
                      ${Boolean(category.isOpen) ? 'max-h-full opacity-100' : 'max-h-0 opacity-0'}
                    `}
                  >
                    {category.subCategories.map(subCategory => (
                      <button
                        key={subCategory.id}
                        className={`
                          w-full text-left py-3 px-8 text-sm
                          ${isSubcategorySelected(category.id, subCategory.id)
                            ? 'text-blue-600 font-medium bg-blue-50'
                            : 'text-gray-700 hover:bg-gray-50'
                          }
                          focus:outline-none transition-colors
                        `}
                        onClick={() => navigateToSubcategory(category.id, subCategory.id, subCategory.name)}
                      >
                        {subCategory.name}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </nav>
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <header className="bg-white shadow-sm z-10 relative">
            {/* Breadcrumbs */}
            {currentCategory && (
              <div className="flex items-center justify-between px-4 py-3 bg-gray-100 border-t border-b text-sm relative">

                {/* Toggle Button on left */}
                <button
                  onClick={() => {
                    trackEvent('Convert Page', 'Click', 'Toggle Sidebar');
                    setSidebarOpen(!sidebarOpen);
                  }}
                  aria-label={sidebarOpen ? "Close sidebar" : "Open sidebar"}
                >
                  {sidebarOpen ? (
                    // Close Icon (X)
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  ) : (
                    // Menu Icon (Hamburger)
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="3" y1="12" x2="21" y2="12" />
                      <line x1="3" y1="6" x2="21" y2="6" />
                      <line x1="3" y1="18" x2="21" y2="18" />
                    </svg>
                  )}
                </button>

                {/* Breadcrumb Text */}
                <div className="flex items-center space-x-2 ml-4 flex-1 overflow-hidden">
                  <span className="text-gray-500 truncate animate-slide-in">CATEGORY</span>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  <span className="text-gray-700 truncate">{currentCategory.name}</span>

                  {currentSubcategory && (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                      <span className="text-blue-600 font-medium truncate">{currentSubcategory.name}</span>
                    </>
                  )}
                </div>
              </div>
            )}
          </header>

          <main className="flex-1 overflow-auto p-6">
            {renderMainContent()}
          </main>
        </div>
      </div>
    </>
  );
};

export default Formatter;